import React, { useState, useCallback, memo } from 'react'
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography } from '@mui/material'
import { Edit, Cancel, Info, Close } from '@mui/icons-material'
import { Order, OrderStatus, OrderType } from '../../proto/generated/order'
import { ExtraSmallButton } from '../custom'
import MoreHorizontalIcon from '../../assets/icons/MoreHorizontalIcon'

interface OrderActionsMenuProps {
    order: Order
    onEdit: () => void
    onCancel: () => void
    onInfo: () => void
    onOpenSlTarget: () => void
    hideCancel?: boolean
}

const OrderActionsMenu: React.FC<OrderActionsMenuProps> = ({ order, onEdit, onCancel, onInfo, onOpenSlTarget, hideCancel = false }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const open = Boolean(anchorEl)

    const canCancelOrModify = order.status && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.INACTIVE, OrderStatus.TRIGGER_PENDING].includes(order.status)

    const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }, [])

    const handleMenuClose = useCallback(() => setAnchorEl(null), [])

    const runAndClose = useCallback(
        (action: () => void) => {
            handleMenuClose()
            action()
        },
        [handleMenuClose]
    )

    const handleCancelConfirm = useCallback(() => {
        setCancelDialogOpen(false)
        onCancel()
    }, [onCancel])

    return (
        <>
            <ExtraSmallButton size="small" onClick={handleMenuOpen} sx={{ p: 0, height: '100%', width: '36px', borderRadius: '0 !important' }}>
                <MoreHorizontalIcon fontSize="small" />
            </ExtraSmallButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                disableAutoFocusItem
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        ml: 1,
                        minWidth: '100px',
                        '& .MuiMenu-list': { paddingTop: 0, paddingBottom: 0 }
                    }
                }}
            >
                <MenuItem onClick={() => runAndClose(onInfo)} sx={{ px: '5px' }}>
                    <ListItemIcon sx={{ minWidth: '30px !important' }}>
                        <Info sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography fontSize={'12px'}>Info</Typography>
                    </ListItemText>
                </MenuItem>

                {canCancelOrModify && (
                    <>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                runAndClose(() => {
                                    if (order.isBasket && [OrderType.STOP_LOSS, OrderType.TARGET].includes(order.orderType)) {
                                        onOpenSlTarget()
                                    } else {
                                        onEdit()
                                    }
                                })
                            }}
                            sx={{ px: '5px' }}
                        >
                            <ListItemIcon sx={{ minWidth: '30px !important' }}>
                                <Edit sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography fontSize={'12px'}>Modify</Typography>
                            </ListItemText>
                        </MenuItem>

                        {!hideCancel && (
                            <MenuItem
                                onClick={() => {
                                    runAndClose(() => setCancelDialogOpen(true))
                                }}
                                sx={{ px: '5px' }}
                            >
                                <ListItemIcon sx={{ minWidth: '30px !important' }}>
                                    <Cancel sx={{ fontSize: 16 }} />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography fontSize={'12px'}>Cancel</Typography>
                                </ListItemText>
                            </MenuItem>
                        )}
                    </>
                )}
            </Menu>

            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.4, minWidth: 300 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Cancel Order
                    <IconButton onClick={() => setCancelDialogOpen(false)} size="small" sx={{ ml: 1 }}>
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to cancel the order?</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCancelDialogOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '4px' }}>
                        NO
                    </Button>
                    <Button onClick={handleCancelConfirm} variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: '4px' }}>
                        YES
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default memo(OrderActionsMenu)
